/**
 * Shared backend-field normalizers, used by screening_service, batch_service,
 * monitoring_service, and recent_searches_service alike (same wire quirks
 * show up on every endpoint that echoes a data_source / country field).
 */
import { isoCountryName } from "@/app/(app)/_lib/iso-countries";

/**
 * Normalize a backend field that identifies one or more codes/names into a
 * clean, deduped string array. Handles a plain string, an array, and a CSV
 * string nested inside either — this backend has been observed sending any
 * of the three for genuinely multi-value fields, e.g. `"OFAC,UN-SC"`,
 * `["OFAC", "UN-SC"]`, or `["OFAC,UN-SC"]`.
 */
export function normalizeDelimitedList(raw: unknown): string[] {
  if (raw == null) return [];
  const items = Array.isArray(raw) ? raw : [raw];
  const values = items
    .filter((item): item is string => typeof item === "string")
    .flatMap((item) => item.split(","))
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  return Array.from(new Set(values));
}

/**
 * Map a list of ISO codes to display names, deduped on the raw ISO (uppercased)
 * and preserving first-seen order, joined with ", ". Unmapped ISO codes pass
 * through verbatim (isoCountryName fallback). The ISO-vs-name display choice
 * is an open design call; we keep names here.
 */
export function joinCountryNames(isos: readonly (string | undefined)[]): string {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const raw of isos) {
    const iso = (raw ?? "").trim();
    if (!iso || seen.has(iso.toUpperCase())) continue;
    seen.add(iso.toUpperCase());
    const name = isoCountryName(iso) || iso;
    if (name) names.push(name);
  }
  return names.join(", ");
}

/**
 * Render one `additional_information` value as display text. Recursive: this
 * backend nests objects inside arrays inside objects (e.g. `passport` arrives
 * as `[{value, country}, …]` while `national_id_no` arrives as a bare
 * `{value, country}`), so a single non-recursive pass turned the array form
 * into "[object Object]". Empty branches collapse to "" and are dropped by the
 * callers below, so a `{}` or `[]` never renders as a labelled blank row.
 */
function formatValue(v: unknown): string {
  if (v == null) return "";
  if (Array.isArray(v)) {
    return v
      .map(formatValue)
      .filter((s) => s.length > 0)
      .join(", ");
  }
  if (typeof v === "object") return formatNestedObject(v as Record<string, unknown>);
  return String(v);
}

/** `{value, …rest}` reads as "value (k: v, k: v)"; without `value`, just "k: v". */
function formatNestedObject(obj: Record<string, unknown>): string {
  const { value, ...rest } = obj;
  const extras = Object.entries(rest)
    .map(([k, v]) => [k, formatValue(v)] as const)
    .filter(([, text]) => text.length > 0)
    .map(([k, text]) => `${k}: ${text}`)
    .join(", ");
  const head = formatValue(value);
  if (head) return extras ? `${head} (${extras})` : head;
  return extras;
}

/** Coerce the source-dependent `additional_information` object to string values. */
export function stringifyAdditional(
  obj: Record<string, unknown> | undefined,
): Record<string, string> {
  const out: Record<string, string> = {};
  if (!obj) return out;
  for (const [k, v] of Object.entries(obj)) {
    const text = formatValue(v);
    if (text.length > 0) out[k] = text;
  }
  return out;
}
